import { StyledDrawer } from './style';
import { Button } from '../../Button';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Select, SelectOption, SelectOptionsValueString } from '../../Select';
import { WithoutModalAddressFinder } from '../../AdressSearch/WithoutModal';
import { Form, FormInstance, message, AutoCompleteProps } from 'antd';
import { Input } from '../../Input';
import { WorkingHours } from '../../WorkingHours';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useAuth } from '../../../hooks/useAuth';
import { DefaultHoursProps } from 'admin/components/FarmDeliveries/types';
import EnumSelect from '../../../admin/Selects/EnumSelect';
import { TextArea } from '../../TextArea';
import { ParcelLocation } from 'public/context/CartContext';
import { Spinner } from 'ui/Spinner';
import { Checkbox } from 'ui/Checkbox';
import { options } from 'config/config';
import { AutoComplete } from 'ui/AutoComplete';
import { Icon } from 'ui';

const ADDRESS_FIELD_NAME = 'address';

interface DrawerProps {
  open: boolean;
  onClose?: any;
  closable?: boolean;
  form: FormInstance;
  setReloadCount: () => void;
  isEdit: boolean;
  farmId: number | undefined;
}

export const DrawerCreateDelivery = ({
  open,
  onClose,
  closable = true,
  form,
  setReloadCount,
  isEdit,
  farmId,
}: DrawerProps) => {
  const [deliveryOptions, setDeliveryOptions] =
    useState<SelectOptionsValueString[]>();
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<
    AutoCompleteProps['options']
  >([]);

  const intl = useIntl();
  const { user } = useAuth();

  const buttonLabel = isEdit
    ? intl.formatMessage({ id: 'general.edit' })
    : intl.formatMessage({ id: 'general.add' });

  const deliveryTypeValue: string = Form.useWatch('type', form);

  const handleSearch = (value: string) => {
    if (!value || !options || value.length < 3) {
      setAutoCompleteOptions([]);
      return;
    }

    const filtered = options.outstafDeliveries.filter((delivery) =>
      delivery.label.toLowerCase().includes(value.toLowerCase())
    );

    setAutoCompleteOptions(
      filtered.map((delivery) => ({
        label: delivery.label,
        value: delivery.value,
      }))
    );
  };

  const stableFarmId = useMemo(() => {
    return farmId ?? user?.farmId;
  }, [farmId, user]);

  const { appendData: createDelivery } = useQueryApiClient({
    request: {
      url: '/api/v2/deliveries',
      method: 'POST',
    },
    onSuccess: () => {
      handleClose();
      setReloadCount();
      message.success(
        intl.formatMessage({ id: 'message.success_delivery_added' })
      );
    },
    onError: () => {
      message.error(intl.formatMessage({ id: 'message.error_save' }));
    },
  });

  const { appendData: updateDelivery } = useQueryApiClient({
    request: {
      url: '/api/v2/deliveries/:id?_method=PATCH',
      method: 'POST',
    },
    onSuccess: () => {
      handleClose();
      setReloadCount();
      message.success(
        intl.formatMessage({ id: 'message.success_delivery_updated' })
      );
    },
    onError: () => {
      message.error(intl.formatMessage({ id: 'message.error_save' }));
    },
  });

  const { isLoading: deliveryLoading } = useQueryApiClient({
    request: {
      url: 'api/v2/delivery/izipiziPickupGetLocations',
    },
    onSuccess(response: ParcelLocation[]) {
      const optionsParcels: SelectOptionsValueString[] = response.map(
        (location: ParcelLocation) => ({
          label: location.name ?? '',
          value: location.name ?? '',
        })
      );
      setDeliveryOptions(optionsParcels);
    },
    onError: () => {
      message.error(intl.formatMessage({ id: 'message.error_get' }));
    },
  });

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const { open_hours, ...otherFields } = values;

      let serializedOpenHours: Record<number, DefaultHoursProps> = {};

      if (open_hours && typeof open_hours === 'object') {
        serializedOpenHours = Object.keys(open_hours).reduce(
          (acc: Record<number, DefaultHoursProps>, dayIndex) => {
            const dayData = open_hours[dayIndex];

            const serializedDay: DefaultHoursProps = {
              no: dayData.no ? dayData.no.format('HH:mm') : null,
              lidz: dayData.lidz ? dayData.lidz.format('HH:mm') : null,
            };

            if (dayData.closed !== undefined) {
              serializedDay.closed = dayData.closed ? '1' : '0';
            }

            acc[parseInt(dayIndex, 10) + 1] = serializedDay;
            return acc;
          },
          {}
        );
      }

      const payload = {
        ...otherFields,
        farm_id: stableFarmId,
        open_hours: serializedOpenHours,
      };

      if (isEdit && values.id) {
        updateDelivery(payload, { id: values.id });
      } else {
        createDelivery(payload);
      }
    } catch (e: any) {
      message.error(intl.formatMessage({ id: 'message.error' }));
    }
  };

  const Footer = () => {
    return (
      <div className={'btns'}>
        <Button
          label={intl.formatMessage({ id: 'general.cancel' })}
          onClick={handleClose}
        />
        <Button label={buttonLabel} type={'primary'} onClick={handleSubmit} />
      </div>
    );
  };

  return (
    <StyledDrawer
      open={open}
      onClose={handleClose}
      closable={closable}
      footer={<Footer />}
      width={850}
      className={'styled-drawer-create-delivery'}
      title={
        isEdit
          ? intl.formatMessage({ id: 'general.edit_delivery_title' })
          : intl.formatMessage({ id: 'general.add_delivery_title' })
      }
      closeIcon={<Icon name={'global-close-icon'} />}
      destroyOnClose
    >
      <Spinner spinning={deliveryLoading}>
        <Input hidden name={'id'} />

        <EnumSelect
          label={intl.formatMessage({ id: 'general.delivery_type' })}
          name={'type'}
          code={'DeliveryType'}
          layout={'vertical'}
          validations={'required'}
          onChange={() => {
            form.setFieldValue('name', null);
            form.setFieldValue(ADDRESS_FIELD_NAME, null);
          }}
        />

        <div className={'address-finder'}>
          <div className={'label'}>
            {intl.formatMessage({ id: 'general.address' })}
          </div>
          <WithoutModalAddressFinder
            form={form}
            name={ADDRESS_FIELD_NAME}
            disabled={!['self', 'delivery'].includes(deliveryTypeValue)}
            deliveryTypeValue={deliveryTypeValue}
          />
        </div>

        {['delivery'].includes(deliveryTypeValue) ? (
          <AutoComplete
            onSearch={handleSearch}
            name={'name'}
            options={autoCompleteOptions}
            label={intl.formatMessage({ id: 'general.name' })}
            layout={'vertical'}
          />
        ) : ['self'].includes(deliveryTypeValue) ? (
          <Input
            label={intl.formatMessage({ id: 'general.name' })}
            validations={'required'}
            name={'name'}
            layout={'vertical'}
          />
        ) : ['izipizi'].includes(deliveryTypeValue) ? (
          <Select
            label={intl.formatMessage({ id: 'general.name' })}
            validations={'required'}
            name={'name'}
            layout={'vertical'}
            children={
              deliveryOptions &&
              deliveryOptions.map((options, index) => (
                <SelectOption value={options.value} key={`${index} - opt`}>
                  {options.label}
                </SelectOption>
              ))
            }
          />
        ) : null}

        <Input
          label={intl.formatMessage({ id: 'general.delivery_price' })}
          layout={'vertical'}
          name={'price'}
          type={'number'}
          validations={
            ['self'].includes(deliveryTypeValue)
              ? ['minZeroNumber']
              : ['minZeroNumber', 'required']
          }
        />

        <Input
          label={intl.formatMessage({ id: 'general.phone_number' })}
          layout={'vertical'}
          name={'phone'}
          validations={
            ['self', 'delivery'].includes(deliveryTypeValue)
              ? ['required', 'phoneNumber']
              : 'phoneNumber'
          }
        />

        <Checkbox
          name={'status'}
          label={intl.formatMessage({ id: 'general.status' })}
        />

        {['self', 'delivery'].includes(deliveryTypeValue) && (
          <div className="working-hours">
            <div className="label-wrapper">
              <h4>{intl.formatMessage({ id: 'general.working_hours' })}</h4>
            </div>
            <WorkingHours minuteStep={30} />
          </div>
        )}

        <TextArea
          layout="vertical"
          name="description"
          label={intl.formatMessage({
            id: 'general.additional_info_description',
          })}
          className={'desc'}
        />
      </Spinner>
    </StyledDrawer>
  );
};
